import React, { useEffect, useState } from "react";
import { CheckCircle2, DollarSign, PhoneCall, Truck, Users } from "lucide-react";
import ShipNowForm from "../ShipNow/ShipNow";
import CarrierSignupForm from "../Carriers/CarrierSignupForm";
import "./LandingPage.css";

const LandingPage = () => {
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [isCarrierFormOpen, setIsCarrierFormOpen] = useState(false);

  const testimonials = [
    {
      quote:
        "We switched from a national broker and the difference is night and day. Someone actually answers when we call, and our loads don't fall through anymore.",
      name: "Mike K.",
      title: "Logistics Manager, Manufacturing Company",
      initials: "MK",
    },
    {
      quote:
        "We were seeing rising detention costs. The Sunny Logistics team helped us leverage their trailer pool to ease dock congestion and improve turn times.",
      name: "Sarah L.",
      title: "Director of Transportation, Food Producer",
      initials: "SL",
    },
    {
      quote:
        "They helped us build a routing guide with preferred carriers based on our performance metrics. Compliance went up and service became more consistent.",
      name: "David R.",
      title: "VP Supply Chain, Industrial Manufacturer",
      initials: "DR",
    },
  ];

  const [testimonialIndex, setTestimonialIndex] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setTestimonialIndex((currentIndex) => (currentIndex + 1) % testimonials.length);
    }, 8000);

    return () => clearInterval(interval);
  }, [testimonials.length]);

  const activeTestimonial = testimonials[testimonialIndex];

  return (
    <div className="landing-page">
      <section className="hero">
        <div className="hero-content">
          <h1>
            We Answer the Phone. <span>We Keep Our Word.</span> We Deliver.
          </h1>
          <p>
            Midwest freight without the runaround. A vetted carrier network, real-time tracking, and a
            team that picks up when you call. Real people, real capacity, real results.
          </p>
          <div className="hero-actions">
            <button
              type="button"
              className="btn btn-primary btn-large"
            onClick={() => setIsFormOpen(true)}
          >
              Ship Freight →
            </button>
            <button
              type="button"
              className="btn btn-outline-white btn-large"
              onClick={() => setIsCarrierFormOpen(true)}
            >
              Haul With Us
            </button>
          </div>
          <div className="hero-proof">
            <div className="hero-proof-item">
              <CheckCircle2 className="hero-proof-icon" />
              98.5% on-time delivery
            </div>
            <div className="hero-proof-item">
              <CheckCircle2 className="hero-proof-icon" />
              Quotes in 30 minutes
            </div>
            <div className="hero-proof-item">
              <CheckCircle2 className="hero-proof-icon" />
              24-hour Quick Pay for carriers
            </div>
          </div>
        </div>
      </section>

      <section className="diff-section">
        <div className="diff-content">
          <div className="diff-header">
            <h2>Why People Switch to Sunny</h2>
            <p>We're not the biggest. We're the ones who pick up the phone.</p>
          </div>
          <div className="diff-grid">
            <div className="diff-card">
              <div className="diff-icon">
                <PhoneCall />
              </div>
              <h3>We Actually Answer</h3>
              <p>Call us right now. A real person picks up—not a phone tree, not a callback queue. This is non-negotiable for us.</p>
            </div>
            <div className="diff-card">
              <div className="diff-icon">
                <Truck />
              </div>
              <h3>Reliable Capacity</h3>
              <p>A vetted Midwest network built for consistency. Partners who know your lanes, your facilities, your requirements.</p>
            </div>
            <div className="diff-card">
              <div className="diff-icon">
                <DollarSign />
              </div>
              <h3>Everyone Gets Paid</h3>
              <p>Carriers get paid in 24 hours, no games. Fair rates mean better service for everyone. Simple as that.</p>
            </div>
          </div>
        </div>
      </section>

      <section className="serve-section">
        <div className="serve-content">
          <div className="serve-header">
            <h2>Who We Work With</h2>
            <p>Whether you're moving freight or hauling it, we've got you covered.</p>
          </div>
          <div className="serve-grid">
            <div className="serve-card">
              <Truck className="serve-card-icon" />
              <h3>For Shippers</h3>
              <p>
                Tired of chasing trucks and getting ghosted? We provide reliable Midwest capacity with
                real-time tracking and a dedicated rep who knows your name.
              </p>
              <ul className="serve-benefits">
                <li><CheckCircle2 /> Vetted capacity across your lanes</li>
                <li><CheckCircle2 /> Real-time GPS tracking on every load</li>
                <li><CheckCircle2 /> Quotes returned in under 30 minutes</li>
                <li><CheckCircle2 /> 24/7 support for active shipments</li>
              </ul>
              <button
                type="button"
                className="btn btn-primary"
                onClick={() => setIsFormOpen(true)}
              >
                Learn More →
              </button>
            </div>
            <div className="serve-card">
              <Users className="serve-card-icon" />
              <h3>For Carriers</h3>
              <p>
                Sick of brokers who don't pay, don't call back, and change rates after the fact? We do
                things differently. Fair rates, fast pay, no BS.
              </p>
              <ul className="serve-benefits">
                <li><CheckCircle2 /> 24-hour Quick Pay</li>
                <li><CheckCircle2 /> Consistent Midwest regional lanes</li>
                <li><CheckCircle2 /> No surprise deductions.</li>
                <li><CheckCircle2 /> Dedicated support that answers</li>
              </ul>
              <button
                type="button"
                className="btn btn-primary"
                onClick={() => setIsCarrierFormOpen(true)}
              >
                Join Our Network →
              </button>
            </div>
          </div>
        </div>
      </section>

      <section className="stats-section">
        <div className="stats-content">
          <div className="stats-header">
            <h2>Built on Results, Not Promises</h2>
            <p>Since 2019, we've been proving it load by load.</p>
          </div>
          <div className="stats-grid">
            <div className="stat-card">
              <div className="stat-number">3,000+</div>
              <div className="stat-label">Loads Delivered</div>
            </div>
            <div className="stat-card">
              <div className="stat-number">98.5%</div>
              <div className="stat-label">On-Time Rate</div>
            </div>
            <div className="stat-card">
              <div className="stat-number">22 min</div>
              <div className="stat-label">Avg. Quote Response</div>
            </div>
            <div className="stat-card">
              <div className="stat-number">24hr</div>
              <div className="stat-label">Carrier Quick Pay</div>
            </div>
          </div>
        </div>
      </section>

      <section className="testimonial-section">
        <div className="testimonial-content">
          <p className="testimonial-label">What Our Partners Say</p>
          <div className="testimonial-carousel">
            <p className="testimonial-quote">
              "{activeTestimonial.quote}"
            </p>
            <div className="testimonial-author">
              <div className="author-avatar">{activeTestimonial.initials}</div>
              <div className="author-info">
                <h4>{activeTestimonial.name}</h4>
                <p>{activeTestimonial.title}</p>
              </div>
            </div>
            <div className="testimonial-controls">
              <button
                type="button"
                className="testimonial-arrow"
                aria-label="Previous testimonial"
                onClick={() =>
                  setTestimonialIndex((currentIndex) =>
                    currentIndex === 0 ? testimonials.length - 1 : currentIndex - 1
                  )
                }
              >
                ‹
              </button>
              <div className="testimonial-dots">
                {testimonials.map((testimonial, index) => (
                  <button
                    key={`${testimonial.name}-${index}`}
                    type="button"
                    className={`testimonial-dot ${index === testimonialIndex ? "active" : ""}`}
                    aria-label={`Show testimonial ${index + 1}`}
                    onClick={() => setTestimonialIndex(index)}
                  />
                ))}
              </div>
              <button
                type="button"
                className="testimonial-arrow"
                aria-label="Next testimonial"
                onClick={() =>
                  setTestimonialIndex((currentIndex) => (currentIndex + 1) % testimonials.length)
                }
              >
                ›
              </button>
            </div>
          </div>
        </div>
      </section>

      <section className="how-section">
        <div className="how-content">
          <div className="how-header">
            <h2>Getting Started is Easy</h2>
            <p>No hoops to jump through. Just freight that moves.</p>
          </div>
          <div className="how-steps">
            <div className="how-step">
              <div className="step-number">1</div>
              <h4>Tell Us What You Need</h4>
              <p>Submit a quote request or call us directly. We'll ask the right questions and give you a straight answer.</p>
            </div>
            <div className="how-step">
              <div className="step-number">2</div>
              <h4>Get a Real Quote</h4>
              <p>We'll respond within 30 minutes during business hours. The rate we quote is the rate you pay—no surprises.</p>
            </div>
            <div className="how-step">
              <div className="step-number">3</div>
              <h4>We Handle the Rest</h4>
              <p>Track your shipment in real-time, get proactive updates, and reach us 24/7 if anything comes up.</p>
            </div>
          </div>
        </div>
      </section>

      <section className="cta-section" id="quote">
        <div className="cta-card">
          <h2>Ready to Move Freight?</h2>
          <p>Whether you're shipping or hauling, let's talk.</p>
          <div className="cta-buttons">
            <button
              type="button"
              className="btn btn-primary btn-large"
              onClick={() => setIsFormOpen(true)}
            >
              Get a Quote →
            </button>
            <button
              type="button"
              className="btn btn-outline-white btn-large"
              onClick={() => setIsCarrierFormOpen(true)}
            >
              Join as Carrier
            </button>
          </div>
          <p className="cta-contact">
            Or call us directly at <a href="tel:+13175370657">(317) 537-0657</a>
          </p>
        </div>
      </section>

      <footer className="landing-footer">
        <div className="footer-content">
          <div className="footer-col">
            <h4>Quick Links</h4>
            <ul>
              <li><a href="/">Home</a></li>
              <li><a href="/shippers">Ship with us</a></li>
              <li><a href="/carriers">Register as a Carrier</a></li>
            </ul>
          </div>
          <div className="footer-col">
            <h4>Our Office</h4>
            <address>
              11 MUNICIPAL DRIVE<br />
              SUITE 200<br />
              FISHERS, IN 46038
            </address>
          </div>
          <div className="footer-col">
            <h4>Legal</h4>
            <ul>
              <li><a href="/terms">Terms of Service</a></li>
              <li><a href="/privacy">Privacy Policy</a></li>
            </ul>
          </div>
        </div>
        <div className="footer-bottom">
          <p>© 2026 Sunny Logistics. All rights reserved.</p>
              </div>
      </footer>

      <ShipNowForm open={isFormOpen} onClose={() => setIsFormOpen(false)} />
      <CarrierSignupForm
        open={isCarrierFormOpen}
        onClose={() => setIsCarrierFormOpen(false)}
      />
    </div>
  );
};

export default LandingPage;